import { useEffect, useRef, useState } from "react";

export default function Photo({ src, alt = "", className = "", eager = false, fill = false }) {
    const frame = useRef(null);
    const seen = useRef(src);
    const [near, setNear] = useState(eager);
    const [ready, setReady] = useState(false);

    if (seen.current !== src) {
        seen.current = src;
        setNear(eager);
        setReady(false);
    }

    useEffect(() => {
        if (eager) return undefined;
        const node = frame.current;
        if (!node || typeof IntersectionObserver === "undefined") {
            setNear(true);
            return undefined;
        }
        const observer = new IntersectionObserver((entries) => {
            if (!entries.some((entry) => entry.isIntersecting)) return;
            setNear(true);
            observer.disconnect();
        }, { rootMargin: "480px" });
        observer.observe(node);
        return () => observer.disconnect();
    }, [eager, src]);

    const picture = useRef(null);

    useEffect(() => {
        const img = picture.current;
        if (!img) return undefined;
        if (img.complete && img.naturalWidth > 0) {
            setReady(true);
            return undefined;
        }
        const show = () => setReady(true);
        img.addEventListener("load", show);
        img.addEventListener("error", show);
        return () => {
            img.removeEventListener("load", show);
            img.removeEventListener("error", show);
        };
    }, [near, src]);

    return (
        <span ref={frame} className={`relative block overflow-hidden bg-placeholder ${className}`} style={fill ? { position: "absolute", inset: 0 } : undefined}>
            <span className={`vton-wave pointer-events-none absolute inset-0 transition-opacity duration-500 ${ready ? "opacity-0" : "opacity-100"}`} aria-hidden="true" />
            {near && src ? (
                <img
                    ref={picture}
                    src={src}
                    alt={alt}
                    decoding="async"
                    fetchPriority={eager ? "high" : "auto"}
                    className={`absolute inset-0 h-full w-full object-cover object-top transition-opacity duration-500 ${ready ? "opacity-100" : "opacity-0"}`}
                />
            ) : null}
        </span>
    );
}
